import { computed } from "vue";
import { useRoute } from "vue-router";
import { useHead, useSeoMeta } from "#imports";

/* ====================================================================
 *  useProductSeo
 *  – <title>, og:*, twitter:*, JSON-LD (Product + BreadcrumbList)
 * ==================================================================== */

export function useProductSeo({
  product,
  currentVariant,
  gallery,
  priceToShow,
  inStock,
  siteUrl,
  categories,
}) {
  const route = useRoute();

  /**
   * <title> віддаємо БЕЗ бренду: глобальний `titleTemplate` у
   * nuxt.config — "%s — mulberry'ss", тож бренд дописується сам.
   * Поки метатеги сторінки не працювали (див. коментар до useSeoMeta
   * нижче), цього не було видно; з робочими тегами бренд задвоївся.
   */
  const seoTitle = computed(() => String(product.value?.title || "").trim());

  /** Для og/twitter — навпаки, з брендом: там жодного шаблону немає. */
  const shareTitle = computed(() =>
    seoTitle.value
      ? `${seoTitle.value} — mulberry'ss`
      : "mulberry'ss — інтернет-магазин",
  );

  const seoDescription = computed(() => {
    // Опис товару в Firestore — багаторядковий (склад, щільність, розмір
    // окремими рядками). У meta переноси йдуть як є і псують сніпет,
    // тому склеюємо в один рядок і ріжемо по межі слова, а не посеред.
    const d = String(product.value?.description || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!d) {
      return "mulberry'ss — шовкові наволочки, аксесуари та одяг. Зроблено в Україні.";
    }
    if (d.length <= 160) return d;
    const cut = d.slice(0, 160);
    const lastSpace = cut.lastIndexOf(" ");
    return `${(lastSpace > 100 ? cut.slice(0, lastSpace) : cut).trim()}…`;
  });

  const DEFAULT_OG_IMAGE = `${siteUrl}/og-default.jpg?v=1`;

  /**
   * Абсолютний URL для метатегів: краулери й месенджери відносний шлях
   * не резолвлять. Це не перестраховка — галерея переїжджає на проксі
   * `/api/img/…` (REFACTOR_PLAN п.9г, крок 5), а він відносний саме
   * тому, що в `<img>` потрібен відносний: абсолютний із чужим хостом
   * тихо вимикає оптимізацію на прев'ю-деплої. Тож форму URL розводимо
   * по споживачах: <img> — відносний, метатеги — абсолютний.
   */
  function absolutize(url) {
    const u = String(url || "").trim();
    if (!u) return "";
    if (/^https?:\/\//.test(u)) return u;
    return `${siteUrl}${u.startsWith("/") ? "" : "/"}${u}`;
  }

  const seoImage = computed(() => {
    const g0 =
      Array.isArray(gallery.value) && gallery.value[0] ? gallery.value[0] : "";
    if (g0) return absolutize(g0);
    const p0 =
      Array.isArray(product.value?.images) && product.value.images[0]
        ? product.value.images[0]
        : "";
    if (p0) return absolutize(p0);
    return DEFAULT_OG_IMAGE;
  });

  /** Чи це фото товару, а не глобальна заглушка. */
  const hasOwnImage = computed(() => seoImage.value !== DEFAULT_OG_IMAGE);

  const seoUrl = computed(() => `${siteUrl}${route.path}`);

  /* ====================================================================
   *  ФОРМА ВИКЛИКУ ТУТ ПРИНЦИПОВА (п.9г).
   *
   *  Раніше стояло `useSeoMeta(() => ({…}))` — і сторінка товару не мала
   *  ЖОДНОГО власного метатега. unhead робить
   *  `const { title, titleTemplate, ...meta } = input`, а спред функції
   *  дає порожній об'єкт: ні помилки, ні тегів. Перевірено на проді
   *  21.08 — у всіх товарів <title>, description і og:* були глобальними
   *  дефолтами з nuxt.config.
   *
   *  Значення передаємо ПО-КЛЮЧОВИМИ гетерами, а не `seoTitle.value`:
   *  інакше вони обчисляться один раз у setup і замруть. На сервері це
   *  непомітно, але при зміні варіанта og:image лишився б від
   *  попереднього.
   * ==================================================================== */
  useSeoMeta({
    // Порожній заголовок означає «лишити глобальний дефолт»:
    // undefined тег не чіпає, на відміну від null (див. og:image:* нижче).
    title: () => seoTitle.value || undefined,
    description: () => seoDescription.value,
    ogTitle: () => shareTitle.value,
    ogDescription: () => seoDescription.value,
    ogType: "product",
    ogUrl: () => seoUrl.value,
    ogImage: () => seoImage.value,
    ogImageSecureUrl: () => seoImage.value,
    // Глобальні og:image:* у nuxt.config описують og-default.jpg
    // (1300×867, image/jpeg). Щойно сторінка ставить власне фото, вони
    // починають брехати: розмірів товарних фото ми не зберігаємо,
    // пропорція 3:4, а частина файлів — png. `null` ПРИБИРАЄ тег,
    // `undefined` лишає глобальний на місці — перевірено на unhead
    // 2.0.19, це різна семантика, не синоніми.
    ogImageWidth: () => (hasOwnImage.value ? null : undefined),
    ogImageHeight: () => (hasOwnImage.value ? null : undefined),
    ogImageType: () => (hasOwnImage.value ? null : undefined),
    twitterCard: "summary_large_image",
    twitterTitle: () => shareTitle.value,
    twitterDescription: () => seoDescription.value,
    twitterImage: () => seoImage.value,
  });

  const productJsonLd = computed(() => {
    const p = product.value || {};
    const v = currentVariant.value || null;
    const price = Number(priceToShow.value || 0);
    const availability = inStock.value
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock";

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: String(p.title || ""),
      description: String(p.description || ""),
      image: [seoImage.value].filter(Boolean),
      sku: String(v?.sku || p.sku || ""),
      brand: { "@type": "Brand", name: "mulberry'ss" },
      offers: {
        "@type": "Offer",
        url: seoUrl.value,
        priceCurrency: "UAH",
        price: isFinite(price) ? price : undefined,
        availability,
        itemCondition: "https://schema.org/NewCondition",
      },
    };
  });

  /* ---- JSON-LD: BreadcrumbList ---- */
  const breadcrumbLd = computed(() => {
    const p = product.value || {};
    const catSlug = p.categorySlug || "";
    const cats = (categories && categories.value) || [];
    const found = cats.find((c) => c.slug === catSlug);
    // Dedicated SEO landing pages
    const LANDING_PAGES = { pillowcases: "/catalog/pillowcases" };
    const cat = found
      ? {
          label: found.label,
          path: LANDING_PAGES[catSlug] || `/catalog?cat=${catSlug}`,
        }
      : null;

    const items = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Головна",
        item: `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Каталог",
        item: `${siteUrl}/catalog`,
      },
    ];

    let pos = 3;
    if (cat) {
      items.push({
        "@type": "ListItem",
        position: pos++,
        name: cat.label,
        item: `${siteUrl}${cat.path}`,
      });
    }

    items.push({
      "@type": "ListItem",
      position: pos,
      name: String(p.title || "Товар"),
      item: seoUrl.value,
    });

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items,
    };
  });

  useHead(() => ({
    script: [
      {
        key: "ld-product",
        type: "application/ld+json",
        children: JSON.stringify(productJsonLd.value),
      },
      {
        key: "ld-breadcrumb",
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbLd.value),
      },
    ],
  }));
}
